/**
 * Kategori-Spesifik Metrikler API Servisi
 * Hibrit: API + Fallback + Periyodik Güncelleme
 */

import { supabase } from '@/lib/supabaseClient';
import { fetchWRIWithCache, transformWRIToSuMetrikleri } from './wriAqueductService';

const PROXY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/climate-api-proxy`;
const PROXY_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const CACHE_DURATION = parseInt(import.meta.env.VITE_METRICS_UPDATE_INTERVAL || '604800000'); // 7 gün

// API Keys
const WRI_API_KEY = import.meta.env.VITE_WRI_AQUEDUCT_API_KEY;
const IEA_API_KEY = import.meta.env.VITE_IEA_API_KEY;
const IRENA_API_KEY = import.meta.env.VITE_IRENA_API_KEY;
const CLIMATE_WATCH_API_KEY = import.meta.env.VITE_CLIMATE_WATCH_API_KEY;
const NOAA_API_KEY = import.meta.env.VITE_NOAA_API_KEY;

interface ScenarioParams {
  isinma_2050: number;
  karbon_fiyati_2030: number;
  karbon_fiyati_2050: number;
  emisyon_azaltim_hedefi: number;
  yenilenebilir_enerji_payi: number;
  location?: { lat: number; lon: number };
}

// ============================================
// 1. SU METRİKLERİ (WRI Aqueduct + FAO)
// ============================================
export async function fetchSuMetrikleri(params: ScenarioParams) {
  try {
    console.log('🌊 Su metrikleri çekiliyor...');
    
    // WRI Aqueduct yerel veritabanından çek
    if (params.location) {
      const wriData = await fetchWRIWithCache(
        params.location.lat,
        params.location.lon
      );
      
      if (wriData) {
        console.log('✅ WRI Aqueduct (Yerel DB) verisi alındı');
        return transformWRIToSuMetrikleri(wriData);
      }
    }
    
    // WRI verisi yoksa veya konum yoksa fallback
    console.log('ℹ️ WRI verisi bulunamadı, hesaplanmış değerler kullanılıyor');
    return calculateSuMetrikleri(params);
  } catch (error) {
    console.warn('❌ Su metrikleri hatası, fallback kullanılıyor:', error);
    return calculateSuMetrikleri(params);
  }
}

function calculateSuMetrikleri(params: ScenarioParams) {
  const { isinma_2050, emisyon_azaltim_hedefi } = params;
  
  return {
    su_stresi_2030: isinma_2050 < 1.5 ? 'Düşük' : isinma_2050 < 2.0 ? 'Orta' : isinma_2050 < 2.5 ? 'Yüksek' : 'Kritik',
    su_stresi_2050: isinma_2050 < 1.5 ? 'Orta' : isinma_2050 < 2.0 ? 'Yüksek' : isinma_2050 < 2.5 ? 'Kritik' : 'Aşırı Kritik',
    su_talebi_artisi_2030: isinma_2050 < 1.5 ? 15 : isinma_2050 < 2.0 ? 25 : isinma_2050 < 2.5 ? 40 : 60,
    su_talebi_artisi_2050: isinma_2050 < 1.5 ? 30 : isinma_2050 < 2.0 ? 50 : isinma_2050 < 2.5 ? 80 : 120,
    endustriyel_su_kullanimi_degisimi: emisyon_azaltim_hedefi > 70 ? -30 : emisyon_azaltim_hedefi > 50 ? -15 : emisyon_azaltim_hedefi > 30 ? 0 : 20,
    yagis_degiskenlik_artisi: isinma_2050 < 1.5 ? 15 : isinma_2050 < 2.0 ? 25 : isinma_2050 < 2.5 ? 40 : 60,
    kuraklik_riski: isinma_2050 < 1.5 ? 'Düşük' : isinma_2050 < 2.0 ? 'Orta' : isinma_2050 < 2.5 ? 'Yüksek' : 'Çok Yüksek',
    sel_riski: isinma_2050 < 1.5 ? 'Düşük' : isinma_2050 < 2.0 ? 'Orta' : isinma_2050 < 2.5 ? 'Yüksek' : 'Çok Yüksek',
    su_kalitesi_bozulma: isinma_2050 < 1.5 ? 10 : isinma_2050 < 2.0 ? 20 : isinma_2050 < 2.5 ? 35 : 50,
    yeraltı_suyu_azalma: isinma_2050 < 1.5 ? 15 : isinma_2050 < 2.0 ? 30 : isinma_2050 < 2.5 ? 50 : 70
  };
}

// ============================================
// 2. ENERJİ METRİKLERİ (IEA + IRENA)
// ============================================
export async function fetchEnerjiMetrikleri(params: ScenarioParams) {
  try {
    console.log('⚡ Enerji metrikleri API\'den çekiliyor...');
    
    // IEA API key varsa kullan
    if (IEA_API_KEY) {
      const response = await fetch(PROXY_URL, {
        method: 'POST',
        signal: AbortSignal.timeout(8000),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${PROXY_KEY}`
        },
        body: JSON.stringify({
          api: 'iea_data',
          endpoint: '/stats',
          params: {
            api_key: IEA_API_KEY
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ IEA API başarılı');
        return transformIEAData(data, params);
      }
    }
    
    console.log('ℹ️ IEA API key yok, hesaplanmış değerler kullanılıyor');
    return calculateEnerjiMetrikleri(params);
  } catch (error) {
    console.warn('❌ Enerji API hatası, fallback kullanılıyor:', error);
    return calculateEnerjiMetrikleri(params);
  }
}

function transformIEAData(data: any, params: ScenarioParams) {
  // IEA API verisini dönüştür
  return calculateEnerjiMetrikleri(params);
}

function calculateEnerjiMetrikleri(params: ScenarioParams) {
  const { yenilenebilir_enerji_payi, karbon_fiyati_2030, karbon_fiyati_2050, emisyon_azaltim_hedefi } = params;
  
  return {
    yenilenebilir_enerji_payi_2030: yenilenebilir_enerji_payi > 80 ? 70 : yenilenebilir_enerji_payi > 60 ? 50 : yenilenebilir_enerji_payi > 40 ? 35 : 25,
    yenilenebilir_enerji_payi_2050: yenilenebilir_enerji_payi,
    enerji_fiyati_2030: karbon_fiyati_2030 > 100 ? 150 : karbon_fiyati_2030 > 70 ? 120 : karbon_fiyati_2030 > 40 ? 100 : 90,
    enerji_fiyati_2050: karbon_fiyati_2050 > 200 ? 200 : karbon_fiyati_2050 > 150 ? 160 : karbon_fiyati_2050 > 100 ? 130 : 110,
    enerji_verimlilik_artisi_2030: emisyon_azaltim_hedefi > 70 ? 25 : emisyon_azaltim_hedefi > 50 ? 18 : emisyon_azaltim_hedefi > 30 ? 12 : 5,
    enerji_verimlilik_artisi_2050: emisyon_azaltim_hedefi > 70 ? 50 : emisyon_azaltim_hedefi > 50 ? 35 : emisyon_azaltim_hedefi > 30 ? 20 : 10,
    elektriklesme_orani_2030: yenilenebilir_enerji_payi > 80 ? 45 : yenilenebilir_enerji_payi > 60 ? 35 : yenilenebilir_enerji_payi > 40 ? 28 : 22,
    elektriklesme_orani_2050: yenilenebilir_enerji_payi > 80 ? 75 : yenilenebilir_enerji_payi > 60 ? 55 : yenilenebilir_enerji_payi > 40 ? 40 : 30,
    enerji_depolama_kapasitesi_2030: yenilenebilir_enerji_payi > 80 ? 500 : yenilenebilir_enerji_payi > 60 ? 300 : yenilenebilir_enerji_payi > 40 ? 150 : 80,
    enerji_depolama_kapasitesi_2050: yenilenebilir_enerji_payi > 80 ? 2000 : yenilenebilir_enerji_payi > 60 ? 1200 : yenilenebilir_enerji_payi > 40 ? 600 : 300,
    hidrojen_ekonomisi_payi_2050: yenilenebilir_enerji_payi > 80 ? 25 : yenilenebilir_enerji_payi > 60 ? 15 : yenilenebilir_enerji_payi > 40 ? 8 : 3,
    nukleer_enerji_payi_2050: yenilenebilir_enerji_payi > 80 ? 5 : yenilenebilir_enerji_payi > 60 ? 10 : yenilenebilir_enerji_payi > 40 ? 15 : 20,
    fosil_yakit_payi_2050: yenilenebilir_enerji_payi > 80 ? 10 : yenilenebilir_enerji_payi > 60 ? 25 : yenilenebilir_enerji_payi > 40 ? 45 : 65
  };
}

// ============================================
// 3. TEDARİK ZİNCİRİ METRİKLERİ
// ============================================
export async function fetchTedarikMetrikleri(params: ScenarioParams) {
  try {
    console.log('📦 Tedarik metrikleri API\'den çekiliyor...');
    
    // World Bank Logistics Performance Index
    // https://lpi.worldbank.org/api
    
    return calculateTedarikMetrikleri(params);
  } catch (error) {
    console.warn('❌ Tedarik API hatası, fallback kullanılıyor:', error);
    return calculateTedarikMetrikleri(params);
  }
}

function calculateTedarikMetrikleri(params: ScenarioParams) {
  const { isinma_2050, karbon_fiyati_2030, karbon_fiyati_2050, emisyon_azaltim_hedefi } = params;
  
  return {
    kesinti_riski_2030: isinma_2050 < 1.5 ? 'Düşük' : isinma_2050 < 2.0 ? 'Orta' : isinma_2050 < 2.5 ? 'Yüksek' : 'Kritik',
    kesinti_riski_2050: isinma_2050 < 1.5 ? 'Orta' : isinma_2050 < 2.0 ? 'Yüksek' : isinma_2050 < 2.5 ? 'Kritik' : 'Aşırı Kritik',
    lojistik_maliyet_artisi_2030: karbon_fiyati_2030 > 100 ? 25 : karbon_fiyati_2030 > 70 ? 18 : karbon_fiyati_2030 > 40 ? 12 : 8,
    lojistik_maliyet_artisi_2050: karbon_fiyati_2050 > 200 ? 50 : karbon_fiyati_2050 > 150 ? 35 : karbon_fiyati_2050 > 100 ? 22 : 15,
    tedarikci_cesitlendirme_ihtiyaci: isinma_2050 < 1.5 ? 'Orta' : isinma_2050 < 2.0 ? 'Yüksek' : isinma_2050 < 2.5 ? 'Kritik' : 'Acil',
    teslimat_suresi_degisimi_2030: isinma_2050 < 1.5 ? 10 : isinma_2050 < 2.0 ? 18 : isinma_2050 < 2.5 ? 28 : 40,
    teslimat_suresi_degisimi_2050: isinma_2050 < 1.5 ? 20 : isinma_2050 < 2.0 ? 35 : isinma_2050 < 2.5 ? 55 : 80,
    tedarikci_esg_skoru_etkisi: emisyon_azaltim_hedefi > 70 ? 'Kritik' : emisyon_azaltim_hedefi > 50 ? 'Yüksek' : emisyon_azaltim_hedefi > 30 ? 'Orta' : 'Düşük',
    yerel_tedarik_orani_artisi: isinma_2050 < 1.5 ? 15 : isinma_2050 < 2.0 ? 25 : isinma_2050 < 2.5 ? 40 : 60,
    dijital_tedarik_zinciri_yatirim: emisyon_azaltim_hedefi > 70 ? 'Yüksek' : emisyon_azaltim_hedefi > 50 ? 'Orta' : emisyon_azaltim_hedefi > 30 ? 'Düşük' : 'Minimal',
    dolayli_emisyon_scope3_artisi: emisyon_azaltim_hedefi > 70 ? -40 : emisyon_azaltim_hedefi > 50 ? -20 : emisyon_azaltim_hedefi > 30 ? 0 : 25,
    tedarikci_iflas_riski: isinma_2050 < 1.5 ? 5 : isinma_2050 < 2.0 ? 12 : isinma_2050 < 2.5 ? 22 : 35
  };
}

// ============================================
// 4. PERİYODİK GÜNCELLEME SİSTEMİ
// ============================================
export async function updateAllMetricsFromAPIs() {
  // console.log('🔄 Tüm metrikler API\'lerden güncelleniyor...');
  
  try {
    const { data: scenarios } = await supabase
      .from('senaryo_sablonlari')
      .select('*')
      .not('senaryo_kodu', 'is', null);

    if (!scenarios) return;

    for (const scenario of scenarios) {
      const params: ScenarioParams = {
        isinma_2050: scenario.isinma_2050,
        karbon_fiyati_2030: scenario.karbon_fiyati_2030,
        karbon_fiyati_2050: scenario.karbon_fiyati_2050,
        emisyon_azaltim_hedefi: scenario.emisyon_azaltim_hedefi,
        yenilenebilir_enerji_payi: scenario.yenilenebilir_enerji_payi
      };

      const [suMetrikleri, enerjiMetrikleri, tedarikMetrikleri] = await Promise.all([
        fetchSuMetrikleri(params),
        fetchEnerjiMetrikleri(params),
        fetchTedarikMetrikleri(params)
      ]);

      await supabase
        .from('senaryo_sablonlari')
        .update({
          su_metrikleri: suMetrikleri,
          enerji_metrikleri: enerjiMetrikleri,
          tedarik_metrikleri: tedarikMetrikleri,
          updated_at: new Date().toISOString()
        })
        .eq('id', scenario.id);

      // console.log(`✅ ${scenario.senaryo_kodu} güncellendi`);
    }

    // Son güncelleme zamanını kaydet
    localStorage.setItem('last_metrics_update', Date.now().toString());
    // console.log('✅ Tüm metrikler güncellendi');
  } catch (error) {
    console.error('❌ Metrik güncelleme hatası:', error);
  }
}

// ============================================
// 5. OTOMATIK GÜNCELLEME KONTROLÜ
// ============================================
export async function checkAndUpdateMetrics() {
  try {
    // Auth kontrolü
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return; // Sessiz atla
    }

    const lastUpdate = localStorage.getItem('last_metrics_update');
    
    if (!lastUpdate || Date.now() - parseInt(lastUpdate) > CACHE_DURATION) {
      // console.log('⏰ Metrikler güncellenme zamanı geldi');
      await updateAllMetricsFromAPIs();
    } else {
      const daysAgo = Math.floor((Date.now() - parseInt(lastUpdate)) / (24 * 60 * 60 * 1000));
      // console.log(`✅ Metrikler güncel (${daysAgo} gün önce güncellendi)`);
    }
  } catch (error) {
    console.error('❌ Metrik kontrol hatası:', error);
  }
}
